import { Trans } from '@lingui/react'

import { PRESCRIPTION_TYPE_PRIVAT } from 'shared/consts/productTemplates'
import PriceLabelTooltip, {
  ModalType,
} from 'views/components/organisms/Cart/ProductListing/PriceLabelTooltip'
import './RxPriceLabel.scss'
import type { ErxAndRxProductProps } from 'views/components/organisms/Cart/ProductListing/PriceLabelType'
import {
  isYourAdditionalPayment,
  isYourPrice,
  isYourPriceWithBonus,
  isYourPriceWithFixedPriceDifference,
  isYourPriceWithLifeStyleProduct,
} from 'views/components/organisms/Cart/ProductListing/RxPriceLabel/helper'

interface RxPriceLabelProps {
  product: ErxAndRxProductProps
  evaluatedPrescriptionType: string
}

enum RxPriceLabelKey {
  YOUR_ADDITIONAL_PAYMENT = 'yourAdditionalPayment',
  YOUR_PRICE = 'yourPrice',
  YOUR_PRICE_WITH_FIXED_PRICE_DIFFERENCE = 'yourPriceWithFixedPriceDifference',
  YOUR_PRICE_WITH_LIFE_STYLE_PRODUCT = 'yourPriceWithLifeStyle',
  YOUR_PRICE_WITH_BONUS = 'yourPriceWithBonus',
  DEFAULT = 'default',
}

const RxPriceLabel = ({
  product,
  evaluatedPrescriptionType,
}: RxPriceLabelProps) => {
  const labels: Record<RxPriceLabelKey, JSX.Element> = {
    [RxPriceLabelKey.YOUR_ADDITIONAL_PAYMENT]: (
      <span className="a-RxPriceLabel">
        <Trans id="productPricing.buyBox.yourAdditionalPayment" />
      </span>
    ),
    [RxPriceLabelKey.YOUR_PRICE]: (
      <span className="a-RxPriceLabel">
        <Trans id="productPricing.buyBox.yourPrice" />
      </span>
    ),
    [RxPriceLabelKey.YOUR_PRICE_WITH_FIXED_PRICE_DIFFERENCE]: (
      <span className="a-RxPriceLabel">
        <Trans id="productPricing.buyBox.yourPrice" />
        <PriceLabelTooltip
          modalType={ModalType.FIXED_PRICE}
          product={product}
          isRx
        />
      </span>
    ),
    [RxPriceLabelKey.YOUR_PRICE_WITH_LIFE_STYLE_PRODUCT]: (
      <span className="a-RxPriceLabel">
        <Trans id="productPricing.buyBox.yourPrice" />
        <PriceLabelTooltip
          modalType={ModalType.LIFESTYLE}
          product={product}
          isRx
          hasBonus={evaluatedPrescriptionType === PRESCRIPTION_TYPE_PRIVAT}
        />
      </span>
    ),
    [RxPriceLabelKey.YOUR_PRICE_WITH_BONUS]: (
      <span className="a-RxPriceLabel">
        <Trans id="productPricing.buyBox.yourPrice" />
        <PriceLabelTooltip
          modalType={ModalType.BONUS_ONLY}
          product={product}
          isRx
        />
      </span>
    ),
    [RxPriceLabelKey.DEFAULT]: (
      <span className="a-RxPriceLabel">
        <Trans id="general.label.sum" />
      </span>
    ),
  }

  const conditions = {
    [RxPriceLabelKey.YOUR_ADDITIONAL_PAYMENT]: isYourAdditionalPayment(
      product,
      evaluatedPrescriptionType
    ),
    [RxPriceLabelKey.YOUR_PRICE]: isYourPrice(
      product,
      evaluatedPrescriptionType
    ),
    [RxPriceLabelKey.YOUR_PRICE_WITH_FIXED_PRICE_DIFFERENCE]:
      isYourPriceWithFixedPriceDifference(product, evaluatedPrescriptionType),
    [RxPriceLabelKey.YOUR_PRICE_WITH_LIFE_STYLE_PRODUCT]:
      isYourPriceWithLifeStyleProduct(product, evaluatedPrescriptionType),
    [RxPriceLabelKey.YOUR_PRICE_WITH_BONUS]: isYourPriceWithBonus(
      product,
      evaluatedPrescriptionType
    ),
  }

  const matchingCondition = Object.keys(conditions).find(
    condition => conditions[condition]
  )

  return labels[matchingCondition || RxPriceLabelKey.DEFAULT]
}

export default RxPriceLabel
